import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AcademicCapIcon, CogIcon, LogoutIcon, UserGroupIcon } from '@heroicons/react/outline';
import { useAuth } from '../../../hooks/useAuth';
import { RMS_STAFF_MANAGEMENT_ROLES } from '../../../constants/userTypes';
import { clearRmsDashboardViewState } from '../../utils/rmsDashboardViewState';
import { currentBundleSrc, fetchDeployedBundleSrc } from '../../utils/deployedVersion';
import tdLogo from '../../assets/images/logos/td_logo.png';
import lcsLogo from '../../assets/images/logos/lcs_logo.png';

/**
 * RmsTopNavBar
 * ------------
 * Purpose:
 *   Top navigation bar for RMS provider-facing pages. Provides consistent branding,
 *   breadcrumb navigation, and user actions across all provider pages.
 *
 * Layout:
 *   - Left: ThinkDivergent logo + Partner logo (NHS Lancashire and South Cumbria)
 *   - Center: Breadcrumb navigation showing current location in the app hierarchy
 *   - Right: Settings link, Log out button, User avatar with initials
 *
 * Props:
 *   - breadcrumbs: Array of breadcrumb items, each with { label, path }
 *     The last item is treated as the current page (non-clickable, bold)
 *     Example: [
 *       { label: 'Home', path: '/rms/provider/dashboard' },
 *       { label: 'Patient Overview', path: null }
 *     ]
 *
 * Usage:
 *   Import and place at the top of each provider page, passing appropriate breadcrumbs.
 *   The component handles sign out and navigation automatically.
 */

export default function RmsTopNavBar({ breadcrumbs = [] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, claims, signOut } = useAuth();
  // Staff management is only reachable by RMS Org. Admin / RMS Support (TD-25),
  // so only show the Staff link to them — otherwise clinicians click it and get
  // bounced to sign-in by the route guard.
  const canManageStaff = RMS_STAFF_MANAGEMENT_ROLES.includes(claims?.userType);

  // ─────────────────────────────────────────────────────────────────────────────
  // Animation State
  // ─────────────────────────────────────────────────────────────────────────────

  // Track mounted state for staggered animation on initial render
  // Only "leaf" items (non-root) animate - the root stays static
  const [isMounted, setIsMounted] = useState(false);

  // Set mounted after initial render for entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setIsMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // Deploy detection
  // ─────────────────────────────────────────────────────────────────────────────

  // Hosting auto-deploys several times a working day, and provider tabs stay
  // open across deploys. Compare the bundle this tab is running against the
  // one currently served; when they differ, offer a refresh rather than
  // letting a stale build keep running against moved-on data and backends.
  // Checks on mount, on tab focus, and every 5 minutes; any network failure
  // is silently ignored. Under the dev server there is no hashed bundle, so
  // this never polls there.
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    const runningBundle = currentBundleSrc(document);
    if (!runningBundle) return undefined;
    let cancelled = false;
    const check = async () => {
      const deployed = await fetchDeployedBundleSrc();
      if (!cancelled && deployed && deployed !== runningBundle) {
        setUpdateAvailable(true);
      }
    };
    check();
    const intervalId = setInterval(check, 5 * 60 * 1000);
    window.addEventListener('focus', check);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener('focus', check);
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // User Display
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Generate user initials from display name or email.
   * Falls back to '?' if no user info is available.
   */
  const getUserInitials = () => {
    if (!user) return '?';

    // Try display name first
    if (user.displayName) {
      const parts = user.displayName.trim().split(' ').filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      if (parts.length === 1 && parts[0].length >= 1) {
        return parts[0][0].toUpperCase();
      }
    }

    // Fall back to email
    if (user.email) {
      return user.email[0].toUpperCase();
    }

    return '?';
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Handle sign out - clears auth and navigates to RMS sign-in page.
   */
  const handleSignOut = async () => {
    try {
      // Clear the persisted dashboard tab selection so the next provider on this
      // browser tab starts from the default view rather than inheriting theirs.
      clearRmsDashboardViewState();
      await signOut();
      navigate('/rms/provider-sign-in', { replace: true });
    } catch (error) {
      console.error('[RmsTopNavBar] Sign out error:', error);
      // Still navigate even if sign out fails
      navigate('/rms/provider-sign-in', { replace: true });
    }
  };

  /**
   * Navigate to breadcrumb path if it's not the current page.
   */
  const handleBreadcrumbClick = (path) => {
    if (path && path !== location.pathname) {
      navigate(path);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <>
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Left section: Logos */}
          <div className="flex items-center gap-4">
            {/* ThinkDivergent logo - clickable, goes to dashboard */}
            <button
              type="button"
              onClick={() => navigate('/rms/provider/dashboard')}
              className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
              aria-label="Go to dashboard"
            >
              <img
                src={tdLogo}
                alt="ThinkDivergent"
                className="h-8 w-auto"
              />
            </button>

            {/* Divider */}
            <div className="h-8 w-px bg-gray-300" />

            {/* Partner logo (NHS Lancashire and South Cumbria) */}
            <img
              src={lcsLogo}
              alt="NHS Lancashire and South Cumbria"
              className="h-10 w-auto"
            />            

          </div>

          {/* Breadcrumbs section - left aligned with animations */}
          <div className="flex-1 flex items-center justify-start px-4 overflow-hidden">
            <nav aria-label="Breadcrumb" className="flex items-center">
              <ol className="flex items-center text-sm">
                {breadcrumbs.map((crumb, index) => {
                  const isFirst = index === 0;
                  const isLast = index === breadcrumbs.length - 1;
                  const isClickable = crumb.path && !isLast;
                  
                  // Only animate non-root items (leaves)
                  const shouldAnimate = !isFirst;
                  
                  // Staggered delay for entrance animation (offset by 1 since root doesn't animate)
                  const animationDelay = `${(index - 1) * 75}ms`;

                  return (
                    <li
                      key={`${crumb.label}-${index}`}
                      className={`flex items-center ${
                        shouldAnimate
                          ? `transition-all duration-300 ease-out ${
                              isMounted
                                ? 'opacity-100 translate-x-0'
                                : 'opacity-0 -translate-x-2'
                            }`
                          : ''
                      }`}
                      style={shouldAnimate ? { transitionDelay: isMounted ? '0ms' : animationDelay } : undefined}
                    >
                      {/* Separator with fade animation (only for non-root items) */}
                      {index > 0 && (
                        <span
                          className={`mx-2 text-gray-400 ${
                            shouldAnimate
                              ? `transition-opacity duration-200 ${isMounted ? 'opacity-100' : 'opacity-0'}`
                              : ''
                          }`}
                          style={shouldAnimate ? { transitionDelay: isMounted ? '0ms' : animationDelay } : undefined}
                        >
                          /
                        </span>
                      )}

                      {isClickable ? (
                        // Clickable breadcrumb - same padding as pill for consistent alignment
                        <button
                          type="button"
                          onClick={() => handleBreadcrumbClick(crumb.path)}
                          className="px-3 py-1 text-gray-500 hover:text-gray-700 hover:underline transition-colors duration-200 rounded-full"
                        >
                          {crumb.label}
                        </button>
                      ) : isLast ? (
                        // Current page - styled as a pill/badge with scale animation (only if not root)
                        <span
                          className={`px-3 py-1 bg-primary/10 text-primary font-semibold rounded-full ${
                            shouldAnimate
                              ? `transition-all duration-300 ease-out ${
                                  isMounted
                                    ? 'opacity-100 scale-100'
                                    : 'opacity-0 scale-95'
                                }`
                              : ''
                          }`}
                          style={shouldAnimate ? { transitionDelay: isMounted ? '0ms' : `${((index - 1) * 75) + 100}ms` } : undefined}
                        >
                          {crumb.label}
                        </span>
                      ) : (
                        // Non-clickable, non-current item - same padding for alignment
                        <span className="px-3 py-1 text-gray-500 transition-colors duration-200">
                          {crumb.label}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>

          {/* Right section: Staff, Settings, Log out, User avatar */}
          <div className="flex items-center gap-1">
            {/* Staff link — only for roles that can reach staff management (TD-25) */}
            {canManageStaff && (
              <button
                type="button"
                onClick={() => navigate('/rms/provider/staff')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-primary/10 rounded-full transition-all duration-200"
              >
                <UserGroupIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Staff</span>
              </button>
            )}

            {/* Schools link — same roles as staff management (SENCO registration) */}
            {canManageStaff && (
              <button
                type="button"
                onClick={() => navigate('/rms/provider/schools')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-primary/10 rounded-full transition-all duration-200"
              >
                <AcademicCapIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Schools</span>
              </button>
            )}

            {/* Settings link */}
            <button
              type="button"
              onClick={() => navigate('/rms/provider/settings')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-primary/10 rounded-full transition-all duration-200"
            >
              <CogIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Settings</span>
            </button>

            {/* Log out link */}
            <button
              type="button"
              onClick={handleSignOut}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-primary/10 rounded-full transition-all duration-200"
            >
              <LogoutIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Log out</span>
            </button>

            {/* User avatar */}
            <div
              className="flex items-center justify-center w-9 h-9 ml-2 rounded-full bg-primary text-white text-sm font-semibold"
              title={user?.displayName || user?.email || 'User'}
            >
              {getUserInitials()}
            </div>
          </div>
        </div>
      </div>
    </nav>
    {/* Deploy-detection banner: sits directly under the sticky nav. The copy
        warns about unsubmitted triage-decision progress because a refresh
        discards the wizard's in-memory answers. */}
    {updateAvailable && (
      <div role="status" className="sticky top-16 z-40 border-b border-amber-200 bg-amber-50 px-6 py-2 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between gap-3">
          <p className="text-left text-sm text-amber-800">
            A new version of the application is available. Refresh to update. If you are midway
            through recording a triage decision, complete and submit it first - unsubmitted
            progress is discarded on refresh.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="whitespace-nowrap rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90"
          >
            Refresh now
          </button>
        </div>
      </div>
    )}
    </>
  );
}
